import React, { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import Ornament from "@/components/Ornament";
import AvailabilityCalendar from "@/components/AvailabilityCalendar";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { api, AIRBNB_URL, CONTACT_EMAIL } from "@/lib/config";
import { Mail, ArrowRight, Loader2 } from "lucide-react";

function parseDate(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function eachDate(start, endExclusive) {
  const out = [];
  const cur = new Date(start);
  while (cur < endExclusive) {
    out.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const PRESETS = {
  archive: {
    purpose: "A visit to the Kit Hesketh-Harvey Archive",
    message: "I'd love to arrange a visit to the Archive Room. ",
  },
};

export default function Enquire() {
  const [params] = useSearchParams();
  const preset = PRESETS[params.get("purpose")] || {};
  const [blocked, setBlocked] = useState([]);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [form, setForm] = useState({
    name: "", email: "", phone: "", guests: "", purpose: preset.purpose || "", message: preset.message || "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/availability")
      .then((r) => setBlocked(r.data?.blocked || []))
      .catch(() => {});
  }, []);

  const disabledDates = useMemo(() => {
    const dates = [];
    for (const b of blocked) {
      const s = parseDate(b.start);
      const e = parseDate(b.end);
      if (!s || !e) continue;
      dates.push(...eachDate(s, e));
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return [{ before: today }, ...dates];
  }, [blocked]);

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please include your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        arrival: range.from ? range.from.toISOString().slice(0, 10) : null,
        departure: range.to ? range.to.toISOString().slice(0, 10) : null,
        guests: form.guests ? Number(form.guests) : null,
        purpose: form.purpose || null,
        message: form.message,
      };
      await api.post("/enquiries", payload);
      toast.success("Your letter is on its way.");
      setForm({ name: "", email: "", phone: "", guests: "", purpose: "", message: "" });
      setRange({ from: undefined, to: undefined });
    } catch (err) {
      toast.error("Something went awry. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Navigation />

      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-8">
        <div className="eyebrow">Correspondence</div>
        <h1 className="press-title text-5xl md:text-6xl text-ink mt-3">Enquire</h1>
        <p className="font-serif text-lg text-ink/80 max-w-2xl mt-5 leading-relaxed">
          Tell us a little about your visit and we'll reply personally — usually within a day.
          For live availability, please{" "}
          <a href={AIRBNB_URL} target="_blank" rel="noopener noreferrer" className="text-burgundy underline underline-offset-4" data-testid="enquire-airbnb-link">
            check the Airbnb listing
          </a>.
        </p>
        <Ornament label="~" />
      </section>

      <section className="max-w-3xl mx-auto px-6 md:px-12 pb-16">
          {/* FORM */}
          <form className="space-y-8" onSubmit={onSubmit} data-testid="enquiry-form">
            <div className="grid md:grid-cols-2 gap-6">
              <LedgerField label="Your Name" required>
                <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} className="ledger-input" data-testid="enquiry-name" />
              </LedgerField>
              <LedgerField label="Email" required>
                <Input type="email" value={form.email} onChange={(e) => handleChange("email", e.target.value)} className="ledger-input" data-testid="enquiry-email" />
              </LedgerField>
              <LedgerField label="Telephone">
                <Input value={form.phone} onChange={(e) => handleChange("phone", e.target.value)} className="ledger-input" data-testid="enquiry-phone" />
              </LedgerField>
              <LedgerField label="Guests (up to 12)">
                <Input type="number" min="1" max="12" value={form.guests} onChange={(e) => handleChange("guests", e.target.value)} className="ledger-input" data-testid="enquiry-guests" />
              </LedgerField>
            </div>

            <LedgerField label="Purpose of the visit">
              <Input
                value={form.purpose}
                onChange={(e) => handleChange("purpose", e.target.value)}
                placeholder="A residency, a birthday, a pilgrimage…"
                className="ledger-input"
                data-testid="enquiry-purpose"
              />
            </LedgerField>

            <LedgerField label="Your letter" required>
              <Textarea
                rows={6}
                value={form.message}
                onChange={(e) => handleChange("message", e.target.value)}
                placeholder="Tell us who is coming, what you'd like to do, any dates you have in mind…"
                className="ledger-input !h-auto"
                data-testid="enquiry-message"
              />
            </LedgerField>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 border-t border-ink/20 pt-6">
              <button
                type="submit"
                disabled={submitting}
                className="bg-burgundy text-parchment px-8 py-4 font-sans text-[0.78rem] uppercase tracking-[0.28em] hover:bg-ink transition-colors inline-flex items-center gap-2 disabled:opacity-60"
                data-testid="enquiry-submit"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : <>Send letter <ArrowRight className="w-4 h-4" strokeWidth={1.5} /></>}
              </button>
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="font-sans text-[0.72rem] uppercase tracking-[0.22em] text-ink/70 hover:text-burgundy inline-flex items-center gap-2"
                data-testid="enquiry-email-direct"
              >
                <Mail className="w-4 h-4" strokeWidth={1.5} /> Or write direct: {CONTACT_EMAIL}
              </a>
            </div>

            {range.from && (
              <div className="font-serif text-ink/75 text-sm border-t border-ink/15 pt-4" data-testid="selected-dates">
                Proposed dates:{" "}
                <span className="text-ink">
                  {range.from.toLocaleDateString()} {range.to ? `→ ${range.to.toLocaleDateString()}` : "(select departure)"}
                </span>
              </div>
            )}
          </form>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 pb-24 border-t border-ink/15 pt-16" data-testid="enquire-availability">
        <AvailabilityCalendar />
      </section>

      <style>{`
        .ledger-input {
          border: none !important;
          border-bottom: 1px solid rgba(26,24,24,0.3) !important;
          border-radius: 0 !important;
          background: transparent !important;
          padding-left: 0 !important;
          padding-right: 0 !important;
          box-shadow: none !important;
          font-family: Georgia, 'Times New Roman', serif !important;
          font-size: 1rem !important;
        }
        .ledger-input:focus, .ledger-input:focus-visible {
          outline: none !important;
          border-bottom: 2px solid #7A2626 !important;
          box-shadow: none !important;
        }
      `}</style>

      <Footer />
    </>
  );
}

function LedgerField({ label, required, children }) {
  return (
    <div>
      <Label className="eyebrow mb-2 block">
        {label}{required && <span className="text-burgundy"> *</span>}
      </Label>
      {children}
    </div>
  );
}
